import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Building, DollarSign, Activity, ShieldAlert } from 'lucide-react'
import { formatCurrency } from '@/lib/pricing'

export default function Management() {
  const { profile } = useAuth()
  const { toast } = useToast()

  const [parkingLots, setParkingLots] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOccupancy: 0,
    totalCapacity: 0,
  })
  const [isAddLotOpen, setIsAddLotOpen] = useState(false)
  const [newLot, setNewLot] = useState({
    name: '',
    address: '',
    total_spots: 50,
  })
  const [editUser, setEditUser] = useState<any>(null)

  const [isAddUserOpen, setIsAddUserOpen] = useState(false)
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'operator',
    parking_lot_id: 'none',
  })

  const fetchData = async () => {
    const { data: lots } = await supabase.from('parking_lots').select('*')
    if (lots) setParkingLots(lots)

    const { data: profiles } = await supabase
      .from('profiles')
      .select('*, user_parking_lots(parking_lot_id)')
    if (profiles) setUsers(profiles)

    const { data: tickets } = await supabase
      .from('tickets')
      .select('status, parking_lot_id')
    const { data: payments } = await supabase
      .from('payments')
      .select('amount, parking_lot_id')

    let revenue = 0
    payments?.forEach((p) => (revenue += Number(p.amount)))

    let occupancy = 0
    tickets?.forEach((t) => {
      if (t.status === 'active') occupancy++
    })

    let capacity = 0
    lots?.forEach((l) => (capacity += Number(l.total_spots)))

    setStats({
      totalRevenue: revenue,
      totalOccupancy: occupancy,
      totalCapacity: capacity,
    })
  }

  useEffect(() => {
    if (profile?.role === 'ultramaster' || profile?.role === 'admin') {
      fetchData()
    }
  }, [profile])

  const handleAddLot = async (e: React.FormEvent) => {
    e.preventDefault()
    const { error } = await supabase.from('parking_lots').insert([newLot])
    if (error) {
      toast({
        title: 'Erro',
        description: error.message,
        variant: 'destructive',
      })
    } else {
      toast({ title: 'Sucesso', description: 'Estacionamento criado!' })
      setIsAddLotOpen(false)
      fetchData()
    }
  }

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    const { error } = await supabase.rpc('create_user_by_admin', {
      p_email: newUser.email,
      p_password: newUser.password,
      p_name: newUser.name,
      p_role: newUser.role,
      p_parking_lot_id:
        newUser.parking_lot_id === 'none' ? null : newUser.parking_lot_id,
    })

    if (error) {
      toast({
        title: 'Erro',
        description: error.message,
        variant: 'destructive',
      })
    } else {
      toast({ title: 'Sucesso', description: 'Usuário criado com sucesso!' })
      setIsAddUserOpen(false)
      setNewUser({
        email: '',
        password: '',
        name: '',
        role: 'operator',
        parking_lot_id: 'none',
      })
      fetchData()
    }
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editUser) return
    const { error } = await supabase
      .from('profiles')
      .update({
        name: editUser.name,
        role: editUser.role,
        parking_lot_id:
          editUser.parking_lot_id === 'none' ? null : editUser.parking_lot_id,
      })
      .eq('id', editUser.id)

    if (editUser.parking_lot_id !== 'none') {
      await supabase.from('user_parking_lots').upsert({
        user_id: editUser.id,
        parking_lot_id: editUser.parking_lot_id,
      })
    }

    if (error) {
      toast({
        title: 'Erro',
        description: error.message,
        variant: 'destructive',
      })
    } else {
      toast({ title: 'Sucesso', description: 'Usuário atualizado!' })
      setEditUser(null)
      fetchData()
    }
  }

  if (profile?.role !== 'ultramaster' && profile?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center space-y-4 animate-fade-in-up">
        <ShieldAlert className="h-16 w-16 text-destructive" />
        <h2 className="text-2xl font-bold">Acesso Negado</h2>
        <p className="text-muted-foreground">
          Apenas o Gestores podem acessar este painel.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-primary flex items-center gap-3">
          <ShieldAlert className="h-8 w-8" /> Painel de Gestão
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestão de unidades, usuários e performance da plataforma.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-sm border-l-4 border-l-primary bg-primary/5">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-primary">
              Faturamento
            </CardTitle>
            <DollarSign className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-primary">
              {formatCurrency(stats.totalRevenue)}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Estacionamentos Ativos
            </CardTitle>
            <Building className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{parkingLots.length}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ocupação Atual
            </CardTitle>
            <Activity className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">
              {stats.totalOccupancy}{' '}
              <span className="text-lg text-muted-foreground font-medium">
                / {stats.totalCapacity}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalCapacity > 0
                ? Math.round((stats.totalOccupancy / stats.totalCapacity) * 100)
                : 0}
              % capacidade utilizada
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="lots" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 h-12">
          <TabsTrigger value="lots" className="font-bold">
            Unidades
          </TabsTrigger>
          <TabsTrigger value="users" className="font-bold">
            Usuários / Perfis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lots" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold">Unidades Cadastradas</h3>
            {profile.role === 'ultramaster' && (
              <Button
                onClick={() => setIsAddLotOpen(true)}
                className="font-bold shadow-sm"
              >
                Novo Estacionamento
              </Button>
            )}
          </div>
          <Card className="shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Endereço</TableHead>
                    <TableHead>Capacidade</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parkingLots.map((lot) => (
                    <TableRow key={lot.id}>
                      <TableCell className="font-bold">{lot.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {lot.address || 'Não informado'}
                      </TableCell>
                      <TableCell>{lot.total_spots} vagas</TableCell>
                      <TableCell>
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-md text-xs font-bold border border-emerald-200">
                          Ativo
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {parkingLots.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center py-8 text-muted-foreground"
                      >
                        Nenhuma unidade encontrada.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-6 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">Controle de Acessos</h3>
            <Button
              onClick={() => setIsAddUserOpen(true)}
              className="font-bold shadow-sm"
            >
              Novo Usuário
            </Button>
          </div>
          <Card className="shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead>Unidades Vinculadas</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-bold">
                        {u.name || '-'}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-md text-xs font-bold border capitalize ${
                            u.role === 'ultramaster'
                              ? 'bg-primary/10 text-primary border-primary/20'
                              : u.role === 'admin'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-muted text-muted-foreground border-border'
                          }`}
                        >
                          {u.role}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {u.user_parking_lots && u.user_parking_lots.length > 0
                          ? u.user_parking_lots
                              .map(
                                (upl: any) =>
                                  parkingLots.find(
                                    (l) => l.id === upl.parking_lot_id,
                                  )?.name,
                              )
                              .filter(Boolean)
                              .join(', ')
                          : 'Acesso Global / Nenhum'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setEditUser({
                              ...u,
                              parking_lot_id: u.parking_lot_id || 'none',
                            })
                          }
                        >
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isAddLotOpen} onOpenChange={setIsAddLotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Novo Estacionamento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLot} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Nome da Unidade</Label>
              <Input
                required
                value={newLot.name}
                onChange={(e) => setNewLot({ ...newLot, name: e.target.value })}
                placeholder="Estacionamento Matriz"
              />
            </div>
            <div className="space-y-2">
              <Label>Endereço Completo</Label>
              <Input
                value={newLot.address}
                onChange={(e) =>
                  setNewLot({ ...newLot, address: e.target.value })
                }
                placeholder="Rua das Flores, 123"
              />
            </div>
            <div className="space-y-2">
              <Label>Total de Vagas</Label>
              <Input
                type="number"
                required
                min="1"
                value={newLot.total_spots}
                onChange={(e) =>
                  setNewLot({ ...newLot, total_spots: Number(e.target.value) })
                }
              />
            </div>
            <Button type="submit" className="w-full h-12 font-bold mt-2">
              Salvar Unidade
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Novo Usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Nome Completo</Label>
              <Input
                required
                value={newUser.name}
                onChange={(e) =>
                  setNewUser({ ...newUser, name: e.target.value })
                }
                placeholder="João Silva"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                required
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                placeholder="joao@parkko.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Senha</Label>
              <Input
                type="password"
                required
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Função / Papel</Label>
              <Select
                value={newUser.role}
                onValueChange={(v) => setNewUser({ ...newUser, role: v })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operador / Atendente</SelectItem>
                  <SelectItem value="financial">Financeiro</SelectItem>
                  {profile?.role === 'ultramaster' && (
                    <SelectItem value="admin">Administrador</SelectItem>
                  )}
                  {profile?.role === 'ultramaster' && (
                    <SelectItem value="ultramaster">UltraMaster</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Unidade Vinculada Inicial</Label>
              <Select
                value={newUser.parking_lot_id}
                onValueChange={(v) =>
                  setNewUser({ ...newUser, parking_lot_id: v })
                }
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profile?.role === 'ultramaster' && (
                    <SelectItem value="none">
                      Nenhuma (Acesso Global)
                    </SelectItem>
                  )}
                  {parkingLots.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full h-12 font-bold mt-2">
              Criar Usuário
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Acesso de Usuário</DialogTitle>
          </DialogHeader>
          {editUser && (
            <form onSubmit={handleUpdateUser} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  required
                  value={editUser.name || ''}
                  onChange={(e) =>
                    setEditUser({ ...editUser, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Função / Papel</Label>
                <Select
                  value={editUser.role}
                  onValueChange={(v) => setEditUser({ ...editUser, role: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">
                      Operador / Atendente
                    </SelectItem>
                    <SelectItem value="financial">Financeiro</SelectItem>
                    {profile?.role === 'ultramaster' && (
                      <SelectItem value="admin">Administrador</SelectItem>
                    )}
                    {profile?.role === 'ultramaster' && (
                      <SelectItem value="ultramaster">UltraMaster</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidade Ativa Atual</Label>
                <Select
                  value={editUser.parking_lot_id}
                  onValueChange={(v) =>
                    setEditUser({ ...editUser, parking_lot_id: v })
                  }
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {parkingLots.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full h-12 font-bold mt-2">
                Salvar Alterações
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
